import logger from '@/lib/logger';
import { toRawImpedanceResult } from '@/lib/impedance';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { useDeviceStore } from '@/store/useDeviceStore';
import { useDeviceService } from '@/contexts/DeviceServiceContext';
import { useDeviceConnection } from '@/hooks/useDevice';
import { MockDeviceService } from '@/services/device/MockDeviceService';
import { isUnknownDeviceUid } from '@/services/device/constants';
import { buildDeviceStatePayload, saveMeasurementWithFallback } from '../utils/measurementPersistence';
import { useIncubationTimer } from '../ProgressResultsView/hooks/useIncubationTimer';
import { testService } from '@/services/api/testService';
import { usePhaseDisconnectRecovery } from './usePhaseDisconnectRecovery';

export type MeasurementPhase = 'incubation' | 'measuring' | 'completed';
type ViewMode = 'measurement' | 'view';

export interface BlockingError {
  title: string;
  message: string;
  action?: () => void;
  actionLabel?: string;
}

/** Initialises the measurement results structure with ANALYZING placeholders. */
export function initializeMeasurementResults(selectedProfile: any) {
  const pathogens =
    selectedProfile?.channels?.map((ch: any) => ch.pathogenName) ||
    ['Influenza A', 'Influenza B', 'RSV', 'COVID-19'];
  return {
    completedAt: Date.now(),
    channels: pathogens.map((pathogen: string, index: number) => ({
      channel: index + 1,
      pathogen,
      impedance: 0,
      phase: 0,
      result: 'ANALYZING' as const,
    })),
  };
}

interface UseSpecimenMeasurementOptions {
  /** testId from router — overrides wizard store testId for API calls */
  testId: string | null | undefined;
  /** Running mode: view stops measurement orchestration */
  mode: ViewMode;
  /** Navigation/close callback */
  onClose: () => void;
}

export interface UseSpecimenMeasurementResult {
  phase: MeasurementPhase;
  setPhase: React.Dispatch<React.SetStateAction<MeasurementPhase>>;
  isConnected: boolean;
  measurementProgress: number;
  measurementResults: any;
  setMeasurementResults: React.Dispatch<React.SetStateAction<any>>;
  /** true while the device is disconnected mid-measure and auto-recovery is running */
  disconnectedMidMeasure: boolean;
  /** true while probing the device after reconnect (spinner state for DeviceReconnectDialog) */
  measureReconnecting: boolean;
  measureReconnectError: string | null;
  handleMeasureDisconnectReconnect: () => Promise<void>;
  handleMeasureDisconnectCancel: () => Promise<void>;
  showRefreshRecovery: boolean;
  setShowRefreshRecovery: React.Dispatch<React.SetStateAction<boolean>>;
  reconnecting: boolean;
  reconnectError: string | null;
  blockingError: BlockingError | null;
  setBlockingError: React.Dispatch<React.SetStateAction<BlockingError | null>>;
  incubationProgress: number;
  handleIncubationReconnect: () => Promise<void>;
  handleInterruptedResume: (outcome: 'harvested' | 'still-running') => void;
  handleInterruptedDiscard: () => void;
  completeMeasurement: () => void;
  subscribeToRunningSpecimen: () => Promise<void>;
}

export function useSpecimenMeasurement({
  testId,
  mode,
  onClose,
}: UseSpecimenMeasurementOptions): UseSpecimenMeasurementResult {
  const deviceService = useDeviceService();
  const { isConnected } = useDeviceConnection();

  // Wizard store
  const selectedProfile = useMeasurementWizardStore((s) => s.selectedProfile);
  const setSpecimenProgress = useMeasurementWizardStore((s) => s.setSpecimenProgress);
  const setSpecimenResult = useMeasurementWizardStore((s) => s.setSpecimenResult);
  const initialDeviceSnapshot = useMeasurementWizardStore((s) => s.initialDeviceSnapshot);
  const baselineCompletedAt = useMeasurementWizardStore((s) => s.baselineCompletedAt);
  const specimenStartedAt = useMeasurementWizardStore((s) => s.specimenStartedAt);
  const setMeasurementInFlight = useMeasurementWizardStore((s) => s.setMeasurementInFlight);
  const clearMeasurementInFlight = useMeasurementWizardStore((s) => s.clearMeasurementInFlight);
  const deviceUid = useDeviceStore((s) => s.deviceInfo?.uid ?? '');

  const [phase, setPhase] = useState<MeasurementPhase>('incubation');
  const [measurementProgress, setMeasurementProgress] = useState(0);
  const [measurementResults, setMeasurementResults] = useState<any>(null);
  const [showRefreshRecovery, setShowRefreshRecovery] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectError, setReconnectError] = useState<string | null>(null);
  const [blockingError, setBlockingError] = useState<BlockingError | null>(null);

  // Stable ref for onClose so stored callbacks always use the latest version
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const incubationTimerFiredRef = useRef(false);
  const incubationWasConnectedRef = useRef(false);
  const measurementAbortCtrlRef = useRef<AbortController | null>(null);
  const resumeStartedRef = useRef(false);

  const completeMeasurement = useCallback(() => {
    setPhase('completed');
    setSpecimenProgress(100);
  }, [setSpecimenProgress]);

  // Recovery Case A: specimen already harvested before mount — go straight to completed
  useEffect(() => {
    const { specimenResult } = useMeasurementWizardStore.getState();
    if (!specimenResult) return;
    setPhase('completed');
  }, []); // mount only

  // Recovery: detect specimen in-flight on mount and open restore modal
  // Must run BEFORE the incubation timer so resumeStartedRef blocks startMeasurement
  useEffect(() => {
    const inFlight = useMeasurementWizardStore.getState().measurementInFlight;
    logger.warn(
      '[RECOVERY:PRV] Mount — measurementInFlight:',
      inFlight,
      '| resumeStartedRef:',
      resumeStartedRef.current,
    );
    if (inFlight?.phase !== 'specimen') return;
    if (resumeStartedRef.current) return;
    resumeStartedRef.current = true;
    logger.warn('[RECOVERY:PRV] Specimen inFlight detected — opening recovery modal');
    setShowRefreshRecovery(true);
  }, []); // mount only — reads store/refs directly

  // Baseline validation gate (measurement mode only)
  useEffect(() => {
    if (mode === 'view') return;
    if (!baselineCompletedAt && !testId) {
      if (!blockingError || blockingError.title !== 'Baseline Measurement Missing') {
        setBlockingError({
          title: 'Baseline Measurement Missing',
          message: 'Cannot start specimen measurement without a completed baseline.',
          action: () => onCloseRef.current(),
          actionLabel: 'Close Wizard',
        });
      }
      return;
    }
    if (blockingError?.title === 'Baseline Measurement Missing') {
      setBlockingError(null);
    }
  }, [baselineCompletedAt, testId, blockingError, mode]);

  const subscribeToRunningSpecimen = useCallback(async () => {
    const abortCtrl = new AbortController();
    measurementAbortCtrlRef.current = abortCtrl;
    try {
      const completionMsg = await deviceService.subscribeMeasurement({
        abortSignal: abortCtrl.signal,
        onProgress: (progress) => {
          setMeasurementProgress(progress);
          setSpecimenProgress(progress);
        },
      });
      measurementAbortCtrlRef.current = null;

      const deviceInfo = useDeviceStore.getState().deviceInfo;
      const {
        testId: storeTestId,
        initialDeviceSnapshot: storeSnapshot,
        measurementInFlight: storeInFlight,
      } = useMeasurementWizardStore.getState();
      const nowDate = new Date();
      const currentTestId = testId || storeTestId;

      const fakeResult = {
        deviceUid: deviceInfo?.uid ?? '',
        deviceSerialNumber: deviceInfo?.serialNumber ?? 0,
        firmwareVersion: deviceInfo?.firmwareVersion ?? '',
        hardwareVersion: deviceInfo?.hardwareVersion ?? '',
        durationMs: 0,
        impedanceResult: completionMsg.impedanceResult,
        completedAt: nowDate,
      };
      setSpecimenResult(fakeResult as any);

      if (currentTestId && completionMsg.impedanceResult) {
        const payload = {
          impedanceResultJson: toRawImpedanceResult(completionMsg.impedanceResult),
          startedAt: storeInFlight
            ? new Date(storeInFlight.startedAt).toISOString()
            : nowDate.toISOString(),
          completedAt: nowDate.toISOString(),
          deviceUid: deviceInfo?.uid ?? '',
          firmwareVersion: deviceInfo?.firmwareVersion ?? '',
          deviceState: buildDeviceStatePayload(storeSnapshot),
        };
        await saveMeasurementWithFallback(currentTestId, 'specimen', payload as any);
      }

      setMeasurementResults(initializeMeasurementResults(selectedProfile));
      completeMeasurement();
      clearMeasurementInFlight();
    } catch (err) {
      measurementAbortCtrlRef.current = null;
      if (useDeviceStore.getState().isEffectivelyDisconnected()) {
        logger.debug(
          '[ProgressResultsView] subscribeMeasurement threw due to device disconnect — usePhaseDisconnectRecovery will handle it',
        );
        return;
      }
      logger.error('[ProgressResultsView] subscribeMeasurement failed during recovery:', err);
      clearMeasurementInFlight();
      setBlockingError({
        title: 'Measurement Resume Failed',
        message:
          err instanceof Error
            ? err.message
            : 'Failed to resume specimen measurement after reconnect.',
        action: () => onCloseRef.current(),
        actionLabel: 'Close',
      });
    }
  }, [
    deviceService,
    testId,
    selectedProfile,
    completeMeasurement,
    setSpecimenProgress,
    setSpecimenResult,
    clearMeasurementInFlight,
  ]);

  const startMeasurement = useCallback(async () => {
    if (mode === 'view') return;

    if (!isConnected && deviceService instanceof MockDeviceService) {
      try {
        setReconnecting(true);
        await deviceService.connect();
        setReconnecting(false);
      } catch (err) {
        setReconnecting(false);
        setBlockingError({
          title: 'Reader Connection Failed',
          message: 'Failed to connect to the mock device. Please try again.',
          action: () => setReconnecting(true),
          actionLabel: 'Retry Connection',
        });
        return;
      }
    } else if (!isConnected) {
      // DeviceReconnectDialog is already visible. Track that the timer fired
      // so we auto-start once the user reconnects.
      logger.debug(
        '[ProgressResultsView] startMeasurement aborted: device not connected — incubationTimerFiredRef set',
      );
      incubationTimerFiredRef.current = true;
      return;
    }

    try {
      const freshState = await deviceService.getState();
      const processingId = Number(freshState?.state?.processingId ?? 0n);
      if (processingId !== 0) {
        setBlockingError({
          title: 'Device is Busy',
          message:
            'The reader is still running the baseline measurement. Please wait for it to complete before starting specimen measurement.',
        });
        return;
      }
    } catch (err) {
      logger.warn('[ProgressResultsView] Failed to ping device for fresh state:', err);
    }

    setPhase('measuring');
    setSpecimenProgress(0);
    const measurementStartTime = new Date();

    try {
      deviceService.setMeasurementPhase?.('specimen');

      if (!selectedProfile) {
        if (deviceService instanceof MockDeviceService && !testId) {
          logger.warn('[ProgressResultsView] No profile selected - using default for mock/demo');
        } else {
          throw new Error('No measurement profile selected. Cannot run specimen measurement.');
        }
      }

      setMeasurementInFlight({ phase: 'specimen', deviceUid });

      const abortCtrl = new AbortController();
      measurementAbortCtrlRef.current = abortCtrl;
      const result = await deviceService.runMeasurement({
        impedanceExec: selectedProfile!.impedanceExecJson,
        abortSignal: abortCtrl.signal,
        onProgress: (progress) => {
          setMeasurementProgress(progress);
          setSpecimenProgress(progress);
        },
      });
      measurementAbortCtrlRef.current = null;

      setSpecimenResult(result as any);

      if (testId) {
        const payload = {
          impedanceResultJson: toRawImpedanceResult(result.impedanceResult),
          startedAt: measurementStartTime.toISOString(),
          completedAt: result.completedAt.toISOString(),
          deviceUid: result.deviceUid,
          firmwareVersion: result.firmwareVersion,
          deviceState: buildDeviceStatePayload(initialDeviceSnapshot),
        };
        await saveMeasurementWithFallback(testId, 'specimen', payload as any);
      }

      setMeasurementResults(initializeMeasurementResults(selectedProfile));
      completeMeasurement();
      clearMeasurementInFlight();
    } catch (error) {
      measurementAbortCtrlRef.current = null;
      if (useDeviceStore.getState().isEffectivelyDisconnected()) {
        logger.debug(
          '[ProgressResultsView] Measurement threw due to device disconnect — usePhaseDisconnectRecovery will handle it',
        );
        return;
      }
      clearMeasurementInFlight();
      logger.error('[ProgressResultsView] Measurement error:', error);
      setBlockingError({
        title: 'Measurement Failed',
        message:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred during measurement.',
        action: () => {
          setBlockingError(null);
          completeMeasurement();
        },
        actionLabel: 'Continue',
      });
    }
  }, [
    mode,
    selectedProfile,
    completeMeasurement,
    setSpecimenProgress,
    setSpecimenResult,
    deviceService,
    testId,
    isConnected,
    setMeasurementInFlight,
    clearMeasurementInFlight,
    deviceUid,
    initialDeviceSnapshot,
  ]);

  const handleInterruptedResume = useCallback(
    (outcome: 'harvested' | 'still-running') => {
      if (outcome === 'harvested') {
        setMeasurementResults(initializeMeasurementResults(selectedProfile));
        completeMeasurement();
      } else {
        setPhase('measuring');
        subscribeToRunningSpecimen();
      }
    },
    [completeMeasurement, selectedProfile, subscribeToRunningSpecimen],
  );

  const handleInterruptedDiscard = useCallback(() => {
    // Modal has already called cancelTest + clearMeasurementInFlight + resetWizard
    onCloseRef.current();
  }, []);

  // ── In-session measuring disconnect — silent auto-recovery ─────────────────
  // Replaces the MeasurementInterruptedModal on disconnect. When the device
  // drops during measurement we track the fact, show DeviceReconnectDialog, and
  // when USB reconnects we probe silently. Only if data is truly lost do we
  // surface a blocking error — no user confirmation needed for the happy path.

  const handleMeasurementLost = useCallback(async () => {
    const currentTestId = useMeasurementWizardStore.getState().testId;
    if (currentTestId) {
      // Re-check server state immediately before cancelling — the user may have taken a while
      // to click "Discard Test" on the blocking-error dialog, and in that time an in-flight or
      // retried specimen-save for this SAME test could have actually landed. Cancelling on top
      // of a specimen-save that's genuinely still in progress races the two requests server-side
      // (closed there by a per-test transaction+lock — see TestService.SaveSpecimenAsync/
      // CancelTestAsync), but discarding a test that has ALREADY completed is a needless,
      // avoidable data loss this check exists to prevent.
      const alreadyCompleted = await testService
        .getTestById(currentTestId)
        .then((detail) => !!(detail.specimenCompletedAt ?? detail.completedAt))
        .catch((err) => {
          logger.warn('[ProgressResultsView] Failed to re-check test status before discarding:', err);
          return false;
        });

      if (alreadyCompleted) {
        setMeasurementResults(initializeMeasurementResults(selectedProfile));
        completeMeasurement();
        clearMeasurementInFlight();
        onCloseRef.current();
        return;
      }

      await testService.cancelTest(currentTestId).catch((err) => {
        logger.warn('[ProgressResultsView] Failed to cancel lost measurement on backend:', err);
      });
    }
    clearMeasurementInFlight();
    onCloseRef.current();
  }, [clearMeasurementInFlight, completeMeasurement, selectedProfile, setMeasurementResults]);

  const {
    disconnected: disconnectedMidMeasure,
    reconnecting: measureReconnecting,
    reconnectError: measureReconnectError,
    handleReconnect: handleMeasureDisconnectReconnect,
    handleCancel: handleMeasureDisconnectCancel,
  } = usePhaseDisconnectRecovery({
    isActive: phase === 'measuring',
    isConnected,
    showRefreshRecovery,
    onReconnected: async () => {
      try {
        const state = await deviceService.getState();
        const processingId = state?.state?.processingId ?? 0;

        if (processingId > 0) {
          setPhase('measuring');
          subscribeToRunningSpecimen();
          return;
        }

        const currentTestId = useMeasurementWizardStore.getState().testId;
        if (currentTestId) {
          const detail = await testService.getTestById(currentTestId);
          if (!!(detail.specimenCompletedAt ?? detail.completedAt)) {
            setMeasurementResults(initializeMeasurementResults(selectedProfile));
            completeMeasurement();
            clearMeasurementInFlight();
            return;
          }
        }

        // Data lost — show blocking error; return normally so hook clears the dialog
        setBlockingError({
          title: 'Measurement Could Not Resume',
          message:
            'The connection was lost and the measurement data could not be recovered. Please discard this test.',
          action: handleMeasurementLost,
          actionLabel: 'Discard Test',
        });
      } catch (err) {
        logger.warn('[ProgressResultsView] Silent measure recovery probe failed:', err);
        throw new Error('Could not verify device status. Check the USB connection and try again.');
      }
    },
    onCancel: async () => onCloseRef.current(),
  });

  const handleIncubationReconnect = useCallback(async () => {
    setReconnecting(true);
    setReconnectError(null);
    try {
      const success = await deviceService.tryAutoReconnect();
      if (!success) {
        setReconnectError(
          'Could not reconnect automatically. Try unplugging and reconnecting the USB cable.',
        );
        return;
      }
      const reconnectedUid = useDeviceStore.getState().deviceInfo?.uid;
      const expectedUid = useMeasurementWizardStore.getState().measurementInFlight?.deviceUid;
      if (
        !isUnknownDeviceUid(reconnectedUid) &&
        !isUnknownDeviceUid(expectedUid) &&
        reconnectedUid !== expectedUid
      ) {
        logger.warn(
          '[ProgressResultsView] UID mismatch on incubation reconnect — disconnecting wrong reader',
        );
        await deviceService.disconnect();
        setReconnectError(
          'The connected reader does not match the one used for this measurement. Please reconnect the original reader.',
        );
      }
    } catch (err) {
      logger.error('[ProgressResultsView] Incubation reconnect failed:', err);
      setReconnectError('Could not reconnect. Please try again.');
    } finally {
      setReconnecting(false);
    }
  }, [deviceService]);

  // Track when the device first connects in this incubation session
  useEffect(() => {
    if (isConnected && phase === 'incubation') {
      incubationWasConnectedRef.current = true;
    }
  }, [isConnected, phase]);

  // Silent auto-reconnect when device disconnects mid-incubation.
  // Only fires after the device was confirmed connected at least once this session.
  useEffect(() => {
    if (
      !isConnected &&
      incubationWasConnectedRef.current &&
      mode === 'measurement' &&
      phase === 'incubation' &&
      !showRefreshRecovery &&
      !reconnecting
    ) {
      handleIncubationReconnect();
    }
    // handleIncubationReconnect is stable; reconnecting guards against re-entry
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, phase, mode, showRefreshRecovery]);

  // Auto-start after reconnect if the incubation timer already fired
  useEffect(() => {
    if (incubationTimerFiredRef.current && isConnected && phase === 'incubation') {
      logger.debug(
        '[ProgressResultsView] Reconnected after incubation completed — auto-starting measurement',
      );
      incubationTimerFiredRef.current = false;
      startMeasurement();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, phase]);

  // Abort the in-flight measurement immediately on disconnect
  useEffect(() => {
    if (!isConnected && phase === 'measuring' && mode === 'measurement') {
      logger.debug(
        '[ProgressResultsView] Device disconnected during measuring — aborting runMeasurement',
      );
      measurementAbortCtrlRef.current?.abort();
    }
  }, [isConnected, phase, mode]);

  // Incubation timer — elapsed is measured from when the user clicked "Start the test"
  // (specimenStartedAt), NOT from baselineCompletedAt. This ensures the 2-minute countdown
  // begins only at the moment the specimen is applied, regardless of how long Steps 4-5 took.
  // Falls back to baselineCompletedAt only in recovery scenarios where specimenStartedAt
  // was lost (pre-migration data).
  //
  // We pass the raw epoch (ms) rather than a pre-computed elapsedMs so that
  // useIncubationTimer's effect always re-runs when the anchor switches anchors
  // (e.g. specimenStartedAt replacing baselineCompletedAt). If we passed elapsedMs
  // computed via useMemo, both the old and new anchor could produce ~0 ms at
  // render time, making the effect deps appear unchanged and the timer never reset.
  const incubationAnchor = specimenStartedAt ?? baselineCompletedAt;
  const incubationAnchorEpoch = useMemo(() => {
    if (!incubationAnchor) return null;
    const d =
      incubationAnchor instanceof Date
        ? incubationAnchor
        : new Date(incubationAnchor as unknown as string);
    return d.getTime();
  }, [incubationAnchor]);

  const incubationDuration =
    deviceService instanceof MockDeviceService ? 5000 : 120000;

  const shouldRunIncubation = mode === 'measurement' && phase === 'incubation';
  const incubationProgress = useIncubationTimer(
    shouldRunIncubation
      ? () => {
          if (!resumeStartedRef.current) startMeasurement();
        }
      : () => {},
    incubationDuration,
    incubationAnchorEpoch,
  );

  return {
    phase,
    setPhase,
    isConnected,
    measurementProgress,
    measurementResults,
    setMeasurementResults,
    disconnectedMidMeasure,
    measureReconnecting,
    measureReconnectError,
    handleMeasureDisconnectReconnect,
    handleMeasureDisconnectCancel,
    showRefreshRecovery,
    setShowRefreshRecovery,
    reconnecting,
    reconnectError,
    blockingError,
    setBlockingError,
    incubationProgress,
    handleIncubationReconnect,
    handleInterruptedResume,
    handleInterruptedDiscard,
    completeMeasurement,
    subscribeToRunningSpecimen,
  };
}
